import * as yup from 'yup';
import { AssumptionsDTO, FirmSettingsDTO } from './api/useFirmSettingsQuery';
import { assumptionsValidation } from './components/Assumptions/Assumptions.meta';
import { validation as agreementsValidation } from './components/AutoAccountsAgreements/AutoAccountsAgreements.meta';
import { CurrentDistributionOrderDTO } from '../../../AccountDistributionOrderModal/api/useAccountDistributionOptionsQuery';
import { SignaturePageContent } from '../../../ManageSignaturePageModal/ManageSignaturePageModal.meta';

export const validation = yup.object().shape({
  supportTeamEmails: yup
    .array()
    .nullable()
    .of(yup.string().email()),

  agreements: agreementsValidation,
  firmAssumptions: assumptionsValidation,
});

export interface FormAgreement {
  name: string;
  isActive: boolean;
}

export interface FormValues {
  firmLogoFilename: string | null;
  allowAdvisersOverrideFirmLogo: boolean;
  customDisclosures: string;
  customDisclosuresEditableByAdviser: boolean;
  customTermsAndConditions: string;
  allowAdvisersCustomTermsAndConditions: boolean;
  allowAdvisersOverrideAgreements: boolean;
  firmAssumptions: AssumptionsDTO;
  assumptionsEditableByAdviser: boolean;
  supportTeamEmails: string[] | null;
  clientLandingPage: number | null;
  disableAdviserSuggestions: boolean;
  agreements: FormAgreement[];
  allowAssetBucketing: boolean;
  allowClientToCustomizeAssetAssignmentAcrossBuckets: boolean;
  allowAdvisersToDelegateClientLevelAssetBucketingCustomization: boolean;
  allowAdvisersToCustomizeAssetBucketingLayout: boolean;
  allowAddManualAccount: boolean;
  allowAdviserOverrideAddManualAccount: boolean;
  allowBeFi20: boolean;
  hasAcceptedBeFi20Terms: boolean;
  allowPulseCheck: boolean;
  allowPersonalFinancesCsvExport: boolean;
  allowMobileAppPushNotifications: boolean;
  defaultDocumentUploadMobilePushNotifications: boolean;
  defaultNewsfeedMobilePushNotifications: boolean;
  allowAccountNickname: boolean;
  accountDistributionOrder: CurrentDistributionOrderDTO;
  allowAiPdfReader: boolean;
  hasAcceptedAiPdfReaderTerms: boolean;
  allowSignaturePage: boolean;
  allowAdvisersOverrideSignaturePage: boolean;
  signaturePage: SignaturePageContent;
  enableThirdPartySharing: boolean;
}

export const parseDefaultValues = (settings: FirmSettingsDTO): FormValues => ({
  firmLogoFilename: settings.firmLogoFilename,
  allowAdvisersOverrideFirmLogo: settings.allowAdvisersOverrideFirmLogo,
  customDisclosures: settings.customDisclosures || '',
  customDisclosuresEditableByAdviser: settings.customDisclosuresEditableByAdviser,
  customTermsAndConditions: settings.customTermsAndConditions,
  allowAdvisersCustomTermsAndConditions: settings.allowAdvisersCustomTermsAndConditions,
  firmAssumptions: settings.firmAssumptions,
  assumptionsEditableByAdviser: settings.assumptionsEditableByAdviser,
  supportTeamEmails: settings.supportTeamEmails,
  clientLandingPage: settings.clientLandingPage,
  disableAdviserSuggestions: settings.disableAdviserSuggestions,
  agreements: settings.agreements?.map(({ name, isActive }) => ({
    name,
    isActive,
  })),
  allowAdvisersOverrideAgreements: settings.allowAdvisersOverrideAgreements,
  allowAssetBucketing: settings.allowAssetBucketing,
  allowClientToCustomizeAssetAssignmentAcrossBuckets:
    settings.allowClientToCustomizeAssetAssignmentAcrossBuckets,
  allowAddManualAccount: settings.allowAddManualAccount,
  allowAdviserOverrideAddManualAccount: settings.allowAdviserOverrideAddManualAccount,
  allowBeFi20: settings.allowBeFi20,
  hasAcceptedBeFi20Terms: settings.hasAcceptedBeFi20Terms,
  allowAdvisersToDelegateClientLevelAssetBucketingCustomization:
    settings.allowAdvisersToDelegateClientLevelAssetBucketingCustomization,
  allowAdvisersToCustomizeAssetBucketingLayout:
    settings.allowAdvisersToCustomizeAssetBucketingLayout,
  allowPulseCheck: settings.allowPulseCheck,
  allowMobileAppPushNotifications: settings.allowMobileAppPushNotifications,
  defaultDocumentUploadMobilePushNotifications:
    settings.defaultDocumentUploadMobilePushNotifications,
  defaultNewsfeedMobilePushNotifications: settings.defaultNewsfeedMobilePushNotifications,
  allowPersonalFinancesCsvExport: settings.allowPersonalFinancesCsvExport,
  allowAccountNickname: settings.allowAccountNickname,
  accountDistributionOrder: settings.accountDistributionOrder,
  allowAiPdfReader: settings.allowAiPdfReader,
  hasAcceptedAiPdfReaderTerms: settings.hasAcceptedAiPdfReaderTerms,
  allowSignaturePage: settings.allowSignaturePage,
  allowAdvisersOverrideSignaturePage: settings.allowAdvisersOverrideSignaturePage,
  signaturePage: settings.signaturePage,
  enableThirdPartySharing: settings.enableThirdPartySharing,
});

export const parseSubmittedValues = (
  values: FormValues,
  settings: FirmSettingsDTO,
): FirmSettingsDTO => {
  return {
    ...settings,
    ...values,
    agreements: (values.agreements || []).map((formAgreement, index) => ({
      ...settings.agreements[index],
      ...formAgreement,
    })),
    firmAssumptions: {
      ...settings.firmAssumptions,
      ...values.firmAssumptions,
    },
  };
};

export type FormFieldName =
  | 'firmLogoFilename'
  | 'customDisclosures'
  | 'customTermsAndConditions'
  | 'allowAdviserOverrideAddManualAccount'
  | 'mobileAppNotificationsFirm';
